/**
 * Subscribe the active notebook view to per-page realtime events.
 *
 * - Every `page.*` event invalidates the workspace's flat page tree.
 * - `page.updated` for the open page invalidates that page's detail cache
 *   (the editor re-fetches the body, preserving unsaved local edits where
 *   possible — for v1 we just blow away the cache and the user re-edits).
 * - `task.page_attached` / `task.page_detached` invalidate both directions
 *   of the cross-reference.
 */

import { useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { RealtimeEvent } from '@friday/shared/realtime'
import { useRealtime } from '@/lib/ws'
import { invalidateAfterTransfer } from '@/features/transfers/transfers'

interface Opts {
  wsId: string | undefined
  openPageId?: string
}

export function useNotebookSubscription({ wsId, openPageId }: Opts) {
  const qc = useQueryClient()
  const onEvent = useCallback(
    (e: RealtimeEvent) => {
      if (
        e.t === 'page.created' ||
        e.t === 'page.updated' ||
        e.t === 'page.moved' ||
        e.t === 'page.archived' ||
        e.t === 'page.restored' ||
        e.t === 'page.deleted'
      ) {
        void qc.invalidateQueries({ queryKey: ['page-tree', wsId] })
        if (openPageId && 'pageId' in e && e.pageId === openPageId) {
          void qc.invalidateQueries({ queryKey: ['page', wsId, openPageId] })
          void qc.invalidateQueries({ queryKey: ['page-tasks', openPageId] })
        }
        return
      }
      if (e.t === 'transfer.completed') {
        // A page subtree or board left or entered this workspace (FRIDAY-34).
        invalidateAfterTransfer(qc, [wsId])
        return
      }
      if (e.t === 'task.page_attached' || e.t === 'task.page_detached') {
        void qc.invalidateQueries({ queryKey: ['task-page-refs', e.taskId] })
        void qc.invalidateQueries({ queryKey: ['page-tasks', e.pageId] })
        return
      }
    },
    [qc, wsId, openPageId],
  )

  useRealtime({
    wsId,
    subscribe: [
      { kind: 'workspace', id: wsId ?? '' },
      ...(openPageId ? [{ kind: 'page' as const, id: openPageId }] : []),
    ],
    onEvent,
  })
}
